/**
 * TurnoutAccuracyView — predicted vs certified turnout (the Feedback Loop).
 *
 * The other half of Tier 3 #12: the July predictions were persisted before
 * the August primary, the certified canvass is imported afterward, and this
 * view is where the two meet. Rows are the county's own reporting grain
 * (canvass jurisdictions, which can group several precincts), sorted by
 * absolute error so the places the model was most wrong lead.
 *
 * Honesty furniture, deliberately visible:
 *   - the absentee line is countywide, so per-jurisdiction actuals carry a
 *     proportional share of it — the raw election-day count stays in the
 *     table next to the allocation;
 *   - official turnout % and predicted turnout % use different denominators
 *     (county registered vs our roll) and are labeled as such;
 *   - jurisdictions the caller's turf only partially covers are named as
 *     excluded, never silently dropped.
 *
 * Consumes GET /predictions/turnout-accuracy (ANALYTICS_READ_ROLES).
 */
import { useEffect, useState } from 'react';
import {
  fetchTurnoutAccuracy,
  formatPredictionsError,
} from '@/api/predictions';
import type { TurnoutAccuracyResponse } from '@/api/predictions';
import { InfoTip } from '@/components/InfoTip';
import FilterSelect from '@/components/filters/FilterSelect';
import { ViewHeader } from '@/components/ViewHeader';

const DEFAULT_ELECTION_DATE = '2026-08-04'; // August primary — first cycle with both halves
const DEFAULT_ELECTION_TYPE = 'primary';

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border-base bg-bg-raised/50 p-4">
      <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
      </div>
      <div className="text-2xl font-bold text-fg-primary mt-1 font-mono">
        {value}
      </div>
      {hint && <div className="text-xs text-fg-muted mt-1">{hint}</div>}
    </div>
  );
}

export function TurnoutAccuracyView({ onBack: _onBack }: { onBack?: () => void }) {
  const [electionDate, setElectionDate] = useState(DEFAULT_ELECTION_DATE);
  const [electionType, setElectionType] = useState(DEFAULT_ELECTION_TYPE);
  const [data, setData] = useState<TurnoutAccuracyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noActuals, setNoActuals] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setNoActuals(false);
      try {
        const res = await fetchTurnoutAccuracy({ electionDate, electionType });
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((err as any)?.response?.status === 404) {
          // No certified results imported yet — an expected state for every
          // election until someone runs import_turnout_actuals.py, not an
          // error.
          setData(null);
          setNoActuals(true);
        } else {
          setError(formatPredictionsError(err));
        }
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [electionDate, electionType]);

  const rows = data
    ? [...data.jurisdictions].sort(
        (a, b) => Math.abs(b.error) - Math.abs(a.error),
      )
    : [];

  return (
    <div className="space-y-6">
      <ViewHeader
        title="Model Accuracy"
        tagline="Predicted turnout vs the certified canvass, per reporting jurisdiction."
        inline={<InfoTip termId="turnout_accuracy" />}
      />

      {/* Filter strip */}
      <div className="bg-bg-raised/50 border border-border-base rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="turnout-accuracy-election-date"
              className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold"
            >
              Election Date
            </label>
            <input
              id="turnout-accuracy-election-date"
              type="date"
              value={electionDate}
              onChange={(e) => setElectionDate(e.target.value)}
              className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            />
          </div>
          <FilterSelect
            label="Election Type"
            value={electionType}
            options={[
              { value: 'primary', label: 'Primary' },
              { value: 'general', label: 'General' },
              { value: 'special', label: 'Special' },
            ]}
            onChange={(val) => setElectionType(val || DEFAULT_ELECTION_TYPE)}
          />
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse mr-2" />
          <span className="text-fg-secondary">Loading accuracy report…</span>
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">
            Failed to load the accuracy report
          </div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      )}

      {!loading && !error && noActuals && (
        <div className="rounded-xl border border-border-base bg-bg-raised/30 p-8 text-center text-fg-muted">
          <div className="text-sm">
            No certified results have been imported for this election yet.
            Once the county publishes its official canvass, an administrator
            can load it and this report fills in.
          </div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              label="Predicted ballots"
              value={fmtInt(data.predicted_total)}
              hint={
                data.model_version
                  ? `model ${data.model_version}`
                  : undefined
              }
            />
            <SummaryCard
              label="Certified ballots"
              value={fmtInt(data.actual_total)}
              hint={`includes ${fmtInt(data.absentee_ballots)} absentee, allocated proportionally`}
            />
            <SummaryCard
              label="Calibration ratio"
              value={
                data.calibration_ratio !== null
                  ? data.calibration_ratio.toFixed(3)
                  : '—'
              }
              hint="certified ÷ predicted; 1.000 is perfect"
            />
            <SummaryCard
              label="Avg. jurisdiction error"
              value={
                data.mean_abs_error !== null
                  ? `±${fmtInt(data.mean_abs_error)}`
                  : '—'
              }
              hint={
                data.mean_abs_pct_error !== null
                  ? `${fmtPct(data.mean_abs_pct_error)} of ballots cast`
                  : undefined
              }
            />
          </div>

          {/* Excluded jurisdictions (region-scoped callers) */}
          {data.excluded_jurisdictions.length > 0 && (
            <div className="rounded-lg border border-status-warning/50 bg-status-warning-bg/30 px-4 py-3 text-sm text-status-warning">
              Not compared (outside your turf or missing predictions):{' '}
              {data.excluded_jurisdictions.join(', ')}
            </div>
          )}

          {/* Jurisdiction table */}
          <div className="rounded-lg border border-border-base overflow-hidden min-w-0">
            {rows.length === 0 ? (
              <div className="bg-bg-raised/30 p-8 text-center text-fg-muted text-sm">
                Certified results are loaded, but there are no predictions for
                this election to compare them against.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-bg-raised border-b border-border-base">
                      <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Jurisdiction
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Predicted
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Certified
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Election-day
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Error
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Turnout (official)
                      </th>
                      <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-fg-secondary font-semibold">
                        Turnout (predicted)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr
                        key={row.jurisdiction_label}
                        className={`border-b border-border-base/50 ${
                          idx % 2 === 0 ? 'bg-bg-raised/20' : 'bg-bg-base/10'
                        } hover:bg-bg-raised/50 transition`}
                      >
                        <td className="px-4 py-3 text-sm text-fg-primary">
                          {row.jurisdiction_label}
                        </td>
                        <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                          {fmtInt(row.predicted_ballots)}
                        </td>
                        <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                          {fmtInt(row.ballots_cast_allocated)}
                        </td>
                        <td className="px-4 py-3 text-sm text-fg-muted text-right font-mono">
                          {fmtInt(row.ballots_cast_election_day)}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm text-right font-mono font-bold ${
                            Math.abs(row.pct_error ?? 0) > 0.15
                              ? 'text-status-warning'
                              : 'text-fg-primary'
                          }`}
                        >
                          {row.error > 0 ? '+' : ''}
                          {fmtInt(row.error)}
                          {row.pct_error !== null && (
                            <span className="text-fg-muted font-normal">
                              {' '}
                              ({row.pct_error > 0 ? '+' : ''}
                              {fmtPct(row.pct_error, 0)})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                          {fmtPct(row.actual_turnout_pct)}
                        </td>
                        <td className="px-4 py-3 text-sm text-fg-secondary text-right font-mono">
                          {fmtPct(row.predicted_turnout_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="text-sm text-fg-muted space-y-1">
              <div>
                Sorted by absolute error — the model's biggest misses first.
                Positive error means the model over-predicted turnout.
                {data.worst_over_label && (
                  <>
                    {' '}
                    Most over-predicted: {data.worst_over_label}. Most
                    under-predicted: {data.worst_under_label}.
                  </>
                )}
              </div>
              <div>
                "Certified" is the official election-day count plus a
                proportional share of the countywide absentee line. Official
                turnout % uses the county's registered count; predicted
                turnout % uses this roll, which also includes voters the
                county has since removed.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
